<template>
  <div>
    <app-spinner v-if="!group_detail" />
    <template v-else>
      <app-cells position="between">
        <app-cells :indent="false">
          <h1 class="title">
            {{ this.group_detail.name }} ({{ group_detail.clients.length }}) - {{ group_detail.short_itinerary || '' }} ID: {{ $route.params.id }}
          </h1>
          <div class="status-title" :style="{ 'backgroundColor': group_detail.type.color || '#5f5f5f' }">
            {{ group_detail.type.name }}
          </div>
        </app-cells>
        <a
          v-if="is_superuser"
          :href="`${admin_url}safari/group/${$route.params.id}/change/`"
          target="_blank"
          class="btn btn--blue"
        >
          Django
        </a>
      </app-cells>
      <div class="info-cards">
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item">
              <span>Duration:</span> <span>{{ group_detail.duration }} Days</span>
            </div>
            <div class="info-cards__row-item">
              <span>Cars:</span> <span>{{ group_detail.cars_required || '-' }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item">
              <span>Tour start:</span><span>{{ dateFormat(group_detail.tour_start) }}</span>
            </div>
            <div class="info-cards__row-item">
              <span>Tour finish:</span><span>{{ dateFormat(group_detail.tour_end) }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item">
              <span>Story itinerary:</span>
              <a
                v-if="group_detail.itinerary_hash"
                :href="`https://tz.altezza.travel/itinerary/${group_detail.itinerary_hash}`"
                class="info-cards__link"
                target="_blank"
              >
                Open
              </a>
              <span v-else class="status-tag" :style="{'backgroundColor': '#e85c20', 'color': '#fff'}">Missing</span>
            </div>
            <div class="info-cards__row-item">
              <router-link
                v-if="group_detail.reservation"
                :to="{ name: 'reservation-safari-detail', params: { id: group_detail.reservation.id } }"
                class="info-cards__link"
              >
                Edit safari details in Request #ATS - {{ group_detail.reservation.id }}
              </router-link>
            </div>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Start from:</span>
              <span>{{ group_detail.start_from?.short_name || '-' }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Finish at:</span>
              <span>{{ group_detail.finish_at?.short_name || '-' }}</span>
            </div>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row"><strong>Cars and drivers</strong></div>
          <div v-if="cars_loading" class="info-cards__row">Loading...</div>
          <div v-else-if="cars_error" class="info-cards__row">Unable to load car assignments</div>
          <template v-else>
            <div v-for="assignment in car_assignments" :key="assignment.kind + assignment.id" class="info-cards__row">
              <div class="info-cards__row-item">{{ assignment.car?.plate_number || 'Not specified' }}</div>
              <div class="info-cards__row-item">{{ assignment.driver?.full_name || 'Not specified' }}</div>
            </div>
            <div v-if="!car_assignments.length" class="info-cards__row">
              <div class="info-cards__row-item">Not specified</div>
              <div class="info-cards__row-item">Not specified</div>
            </div>
          </template>
        </div>
      </div>
      <app-cells>
        <app-textarea
          v-model="group_detail.notes"
          rows="5"
          @change.native="onUpdateNotes"
          placeholder="Write a notes"
        />
      </app-cells>
      <app-tabs :tabs="tabs_to_show" />
      <transition name="fade" mode="out-in">
        <router-view :groupDetail="group_detail" @getSafariInfo="getSafariInfo" />
      </transition>
    </template>
  </div>
</template>

<script>
import AppTabs from '@/components/AppTabs'
import { format, parse } from 'date-fns'
import { getGoogleSearch, getReservationSafariCars, getSafariGroup, getSafariGroupTypes, patchSafariGroups, postLocation } from '@/http'
import axios from 'axios'
import debounce from 'lodash/debounce'

export default {
  name: 'SafariGroupsDetailLayout',
  components: { AppTabs },
  data() {
    return {
      group_detail: null,
      car_assignments: [],
      cars_loading: false,
      cars_error: false,
      tabs: [
        { name: 'safari-groups-detail-clients', label: 'Clients', roles: ['general_manager', 'operations_manager', 'sales_manager', 'safari_supervisor', 'safari_manager', 'safari_assistant', 'operations_supervisor', 'sales_supervisor'] },
        { name: 'safari-groups-detail-drivers-itinerary', label: 'Driver’s itinerary', roles: ['general_manager', 'safari_supervisor', 'sales_manager', 'sales_supervisor', 'operations_manager', 'operations_supervisor'] },
        { name: 'safari-groups-detail-np', label: 'NP', roles: ['general_manager', 'safari_supervisor', 'operations_supervisor'] },
        { name: 'safari-groups-detail-snacks', label: 'Snack bar', roles: ['general_manager', 'sales_manager', 'safari_supervisor', 'safari_manager', 'safari_assistant', 'operations_supervisor', 'sales_supervisor'] },
        { name: 'safari-groups-detail-cars', label: 'Cars', roles: ['general_manager', 'safari_supervisor', 'sales_supervisor', 'sales_manager', 'operations_supervisor'] },
        { name: 'safari-groups-detail-transfers', label: 'Transfers', roles: ['general_manager', 'sales_manager', 'operations_manager', 'safari_supervisor', 'safari_manager', 'safari_assistant', 'operations_supervisor'] },
        { name: 'safari-groups-detail-downloads', label: 'Downloads', roles: ['general_manager', 'safari_supervisor'] },
      ],
      tabs_to_show: [],
      group_type_options: [],
      file_loading: false,
      location_options: [],
      admin_url: process.env.VUE_APP_ADMIN_URL,
    }
  },
  computed: {
    show_edit_btn() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_manager') ||
        this.$store.state.userGroups.map(item => item.code).includes('general_manager') ||
        this.$store.state.userGroups.map(item => item.code).includes('safari_supervisor') ||
        this.$store.state.userGroups.map(item => item.code).includes('sales_supervisor')
    },
    is_superuser() {
      return this.$store.state.userData.is_superuser
    },
  },
  created() {
    this.getSafariInfo()
    getSafariGroupTypes().then(res => { this.group_type_options = res.data })
  },
  methods: {
    onUpdateNotes() {
      patchSafariGroups(this.$route.params.id, {notes: this.group_detail.notes})
        .then(() => {
          this.getSafariInfo()
          this.$notify({
            type: 'success',
            title: 'Notes saved',
          })
        })
    },
    tabsToShow() {
      const tabs = []
      this.tabs.forEach(item => {
        if (!item.roles.length) {
          tabs.push(item)
          return
        }
        item.roles.forEach(role => {
          if (this.$store.state.userGroups.some(groupRole => groupRole.code === role)) tabs.push(item)
        })
      })
      this.tabs_to_show = tabs
      if (this.group_detail.type.code === 'gamepackage') {
        this.tabs_to_show = this.tabs_to_show.filter(item => item.name !== 'safari-groups-detail-snacks')
      }
      if (!this.group_detail.reservation) {
        this.tabs_to_show = this.tabs_to_show.filter(item => item.name !== 'safari-groups-detail-drivers-itinerary' && item.name !== 'safari-groups-detail-cars')
      }
    },
    dateFormat(date) {
      return format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy')
    },
    getSafariInfo() {
      getSafariGroup(this.$route.params.id).then(response => {
        this.group_detail = response.data
        document.title = `${this.group_detail.name} (${this.group_detail.clients.length}) - ${this.group_detail.short_itinerary || ''} ID: ${ this.$route.params.id } | Altezza Board`
        this.tabsToShow()
        this.loadCarAssignments()
      })
    },
    loadCarAssignments() {
      this.car_assignments = []
      this.cars_error = false
      if (!this.group_detail.reservation) return

      this.cars_loading = true
      getReservationSafariCars(this.group_detail.reservation.id)
        .then(response => {
          this.car_assignments = [
            ...response.data.own_cars.map(car => ({ ...car, kind: 'own' })),
            ...response.data.rental_cars.map(car => ({ ...car, kind: 'rental' })),
          ]
        })
        .catch(() => { this.cars_error = true })
        .finally(() => { this.cars_loading = false })
    },
    onDownloadFile() {
      this.file_loading = true
      axios.get(this.group_detail.driver_itinerary.file, { responseType: 'blob' })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          const extension = this.group_detail.driver_itinerary.file_name.split('.').pop()
          link.href = url
          link.setAttribute('download', `Driver itinerary ${this.group_detail.name} ${this.dateFormat(this.group_detail.tour_start)}-${this.dateFormat(this.group_detail.tour_end)}.${extension}`)
          document.body.appendChild(link)
          link.click()
          URL.revokeObjectURL(url)
          this.file_loading = false
        })
    },
    onLocationSearch(search, loading) {
      loading(true)
      this.searchLocation(loading, search, this)
    },
    searchLocation: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.location_options = []
      } else {
        getGoogleSearch(search).then(response => {
          vm.location_options = response.data
          loading(false)
        })
      }
    }, 350),
  }
}
</script>
